import type * as C from "@zooi/contracts";
import { localDate } from "@zooi/domain";
import type { StatusKind } from "./ui/status-kinds.ts";

// What the product's profile shows under Lessons, and which actions a metric
// chart marks (docs/design-system.md §0.6, §0.8 chart marks). Kept apart from
// the components so the rules are tested.

/** Lessons grouped by what the reading that taught them found; retired lessons last. */
export const LESSON_GROUPS = ["worked", "did_not_work", "no_signal", "inconclusive", "retired"] as const;
export type LessonGroup = (typeof LESSON_GROUPS)[number];

const BY_READING: Record<C.ReadingValueT, LessonGroup> = { positive: "worked", negative: "did_not_work", no_signal: "no_signal", inconclusive: "inconclusive" };

/** The reading a lesson's statement names, for lessons whose action is no longer listed. */
function statementReading(statement: C.Reason): C.ReadingValueT | null {
  const value = statement.params?.reading;
  return value === "positive" || value === "negative" || value === "no_signal" || value === "inconclusive" ? value : null;
}

/**
 * Where a lesson sits: a retired lesson is retired; otherwise by its action's
 * confirmed reading, else the reading its statement names. Without either the
 * honest place is "inconclusive", never "worked".
 */
export function lessonGroup(lesson: Pick<C.LearningT, "status" | "statement">, action?: Pick<C.Action, "reading"> | null): LessonGroup {
  if (lesson.status === "stale") return "retired";
  const value = action?.reading?.value ?? statementReading(lesson.statement);
  return value ? BY_READING[value] : "inconclusive";
}

/** The status icon of a group (§0.3b): the reading's result, a cross for retired. */
export const LESSON_STATUS: Record<LessonGroup, StatusKind> = { worked: "positive", did_not_work: "negative", no_signal: "none", inconclusive: "inconclusive", retired: "stopped" };

/** Statements that only restate the reading: the row says the action instead. */
const RESTATES = new Set(["metric_effect", "operational_acceptance"]);
export const statementAddsDetail = (statement: C.Reason) => !RESTATES.has(statement.code);

export interface LessonRow<L, A> { lesson: L; action: A | null; group: LessonGroup }

/** Each group with its lessons, newest first; empty groups are left out. */
export function groupLessons<L extends Pick<C.LearningT, "id" | "status" | "statement" | "action_id" | "created_at">, A extends Pick<C.Action, "id" | "reading">>(lessons: readonly L[], actions: readonly A[]): { group: LessonGroup; rows: LessonRow<L, A>[] }[] {
  const byId = new Map(actions.map((a) => [a.id, a]));
  const rows = lessons.map((lesson) => {
    const action = lesson.action_id ? byId.get(lesson.action_id) ?? null : null;
    return { lesson, action, group: lessonGroup(lesson, action) };
  }).sort((a, b) => b.lesson.created_at.localeCompare(a.lesson.created_at) || a.lesson.id.localeCompare(b.lesson.id));
  return LESSON_GROUPS.map((group) => ({ group, rows: rows.filter((r) => r.group === group) })).filter((g) => g.rows.length > 0);
}

/**
 * For each period of a chart, the actions that went live in it, by the
 * product's local date. Only when they went live, never what they caused:
 * an effect is a confirmed reading (design/product-vision.md §5).
 */
export function actionMarks<A extends Pick<C.Action, "id" | "activated_at">>(periods: readonly Pick<C.MetricDashboard["periods"][number], "start" | "end">[], actions: readonly A[], timeZone: string): A[][] {
  const live = actions.filter((a) => a.activated_at)
    .map((a) => ({ a, date: localDate(Date.parse(a.activated_at!), timeZone) }))
    .sort((x, y) => x.date.localeCompare(y.date) || x.a.id.localeCompare(y.a.id));
  return periods.map((p) => live.filter((x) => x.date >= p.start && x.date <= p.end).map((x) => x.a));
}
